import { useEffect, useMemo, useState } from "react"
import { Download, Mail } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/shared/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog"
import { Textarea } from "@/shared/components/ui/textarea"
import type { ClaimedByReportFilters } from "../lib/claimed-by-workscope-status"
import { useEmailPartnerReport, usePartnerReportPdf } from "../hooks/use-partner-materials-report"
import { PartnerReportPdfPreview } from "./partner-report-pdf-preview"

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const MAX_PARTNER_REPORT_RECIPIENTS = 10

export function parsePartnerReportEmails(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
}

export function validatePartnerReportEmails(value: string): string | null {
  const emails = parsePartnerReportEmails(value)
  if (emails.length === 0) return "Enter at least one email address."
  if (emails.length > MAX_PARTNER_REPORT_RECIPIENTS) {
    return `Enter no more than ${MAX_PARTNER_REPORT_RECIPIENTS} email addresses.`
  }
  if (emails.some((email) => !EMAIL_PATTERN.test(email))) {
    return "Enter only valid email addresses."
  }
  return null
}

export function buildPartnerReportFilename(
  organizationName: string,
  generatedAt: Date = new Date(),
): string {
  const title = `${organizationName} Status Report`
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "Partner-Status-Report"
  return `${title}-${generatedAt.toISOString().slice(0, 10)}.pdf`
}

type PartnerReportDialogProps = {
  regionId: number
  organizationName: string
  filters: ClaimedByReportFilters
  contactEmail?: string | null
}

export function PartnerReportDialog({
  regionId,
  organizationName,
  filters,
  contactEmail = "",
}: PartnerReportDialogProps) {
  const [open, setOpen] = useState(false)
  const [emailOpen, setEmailOpen] = useState(false)
  const reportRequest = useMemo(
    () => ({ regionId, organizationName, filters }),
    [regionId, organizationName, filters],
  )
  const pdfQuery = usePartnerReportPdf(reportRequest, open)

  const downloadReport = () => {
    if (!pdfQuery.data) return
    const url = URL.createObjectURL(pdfQuery.data)
    const link = document.createElement("a")
    link.href = url
    link.download = buildPartnerReportFilename(organizationName)
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline">Report</Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>ReliefHub Partner Report</DialogTitle>
          <DialogDescription>
            This report reflects the work scope and completion-date filters currently selected.
          </DialogDescription>
        </DialogHeader>

        {pdfQuery.isLoading && (
          <div role="status" className="py-12 text-center text-sm text-muted-foreground">
            Generating partner report...
          </div>
        )}
        {pdfQuery.error && (
          <p role="alert" className="rounded-md border border-destructive/40 p-4 text-sm text-destructive">
            {pdfQuery.error instanceof Error
              ? pdfQuery.error.message
              : "Unable to generate the partner report."}
          </p>
        )}
        {pdfQuery.data && <PartnerReportPdfPreview blob={pdfQuery.data} />}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setEmailOpen(true)}
            disabled={!pdfQuery.data}
          >
            <Mail className="mr-2 h-4 w-4" />
            Email to Partner
          </Button>
          <Button type="button" onClick={downloadReport} disabled={!pdfQuery.data}>
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
        </DialogFooter>

        <EmailPartnerReportDialog
          open={emailOpen}
          onOpenChange={setEmailOpen}
          regionId={regionId}
          organizationName={organizationName}
          filters={filters}
          contactEmail={contactEmail}
        />
      </DialogContent>
    </Dialog>
  )
}

type EmailPartnerReportDialogProps = PartnerReportDialogProps & {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EmailPartnerReportDialog({
  open,
  onOpenChange,
  regionId,
  organizationName,
  filters,
  contactEmail = "",
}: EmailPartnerReportDialogProps) {
  const [emailText, setEmailText] = useState("")
  const [message, setMessage] = useState("")
  const [validationError, setValidationError] = useState("")
  const emailMutation = useEmailPartnerReport()

  useEffect(() => {
    if (!open) return
    setEmailText(contactEmail?.trim() ?? "")
    setMessage("")
    setValidationError("")
    emailMutation.reset()
  }, [contactEmail, open])

  const sendEmail = async () => {
    const error = validatePartnerReportEmails(emailText)
    if (error) {
      setValidationError(error)
      return
    }

    setValidationError("")
    try {
      await emailMutation.mutateAsync({
        regionId,
        organizationName,
        filters,
        recipients: parsePartnerReportEmails(emailText),
        message,
      })
      toast.success("Partner report emailed successfully.")
      onOpenChange(false)
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Unable to email the partner report.")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Email to Partner</DialogTitle>
          <DialogDescription>
            Send the current {organizationName} report to up to {MAX_PARTNER_REPORT_RECIPIENTS} recipients.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label htmlFor="partner-report-emails" className="mb-1 block text-sm font-medium">
              Email addresses
            </label>
            <Textarea
              id="partner-report-emails"
              value={emailText}
              onChange={(event) => setEmailText(event.target.value)}
              placeholder="partner@example.com, teammate@example.com"
              aria-invalid={Boolean(validationError)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Separate multiple addresses with commas, spaces, semicolons, or new lines.
            </p>
            {validationError && <p role="alert" className="mt-1 text-sm text-destructive">{validationError}</p>}
          </div>
          <div>
            <label htmlFor="partner-report-message" className="mb-1 block text-sm font-medium">Message</label>
            <Textarea
              id="partner-report-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Add an optional message for the partner."
              rows={5}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={sendEmail} disabled={emailMutation.isPending}>
            {emailMutation.isPending ? "Sending..." : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

